'use client'

import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useAudio } from './AudioContext'
import { useSubtitle } from './SubtitleContext'
import { useVideo } from './VideoContext'

interface ControlState {
  isPlaying: boolean
  isMuted: boolean
  volume: number
  subtitleEnabled: boolean
}

interface ControlContextType extends ControlState {
  play: () => void
  pause: () => void
  setIsMuted: (isMuted: boolean) => void
  setVolume: (volume: number) => void
  setSubtitleEnabled: (enabled: boolean) => void
}

const initialControlState: ControlState = {
  isPlaying: false,
  isMuted: false,
  volume: 1,
  subtitleEnabled: true,
}

const ControlContext = createContext<ControlContextType | undefined>(undefined)

export function ControlProvider({ children }: { children: ReactNode }) {
  const {
    play: playVideo,
    pause: pauseVideo,
    // Video files should not have audio tracks
    // setMuted: setVideoMuted,
    // setVolume: setVideoVolume,
    isPlaying: videoIsPlaying,
  } = useVideo()
  const {
    play: playAudio,
    pause: pauseAudio,
    setMuted: setAudioMuted,
    setVolume: setAudioVolume,
    isPlaying: audioIsPlaying,
  } = useAudio()
  const { setEnabled: setSubtitleEnabledState } = useSubtitle()

  const [state, setState] = useState<ControlState>(initialControlState)

  // Sync internal isPlaying state with video/audio
  useEffect(() => {
    setState((prev) => ({ ...prev, isPlaying: videoIsPlaying || audioIsPlaying }))
  }, [videoIsPlaying, audioIsPlaying])

  const play = useCallback(() => {
    playVideo()
    playAudio()
    setState((prev) => ({ ...prev, isPlaying: true }))
  }, [playVideo, playAudio])

  const pause = useCallback(() => {
    pauseVideo()
    pauseAudio()
    setState((prev) => ({ ...prev, isPlaying: false }))
  }, [pauseVideo, pauseAudio])

  const setIsMuted = useCallback(
    (isMuted: boolean) => {
      setAudioMuted(isMuted)
      setState((prev) => ({ ...prev, isMuted }))
    },
    [setAudioMuted]
  )

  const setVolumeHandler = useCallback(
    (volume: number) => {
      setAudioVolume(volume)
      setState((prev) => ({ ...prev, volume }))
    },
    [setAudioVolume]
  )

  const setSubtitleEnabled = useCallback(
    (enabled: boolean) => {
      setSubtitleEnabledState(enabled)
      setState((prev) => ({ ...prev, subtitleEnabled: enabled }))
    },
    [setSubtitleEnabledState]
  )

  const value = {
    ...state,
    play,
    pause,
    setIsMuted,
    setVolume: setVolumeHandler,
    setSubtitleEnabled,
  }
  return <ControlContext.Provider value={value}>{children}</ControlContext.Provider>
}

export function useControl() {
  const context = useContext(ControlContext)
  if (context === undefined) {
    throw new Error('useControl must be used within a ControlProvider')
  }
  return context
}
